import { useEffect, useRef } from "react";

import { AnimatePresence, motion } from "framer-motion";

import { Button } from "@/components/reusable";

import type { PipelineStageId } from "@/lib/pipelineBoard";

import { PIPELINE_STAGES } from "@/lib/pipelineBoard";

import type { PipelineFileCardModel } from "@/lib/pipelineBoard";

import { PipelineFileCard } from "./PipelineFileCard";

import { PIPELINE_STAGE_ICONS, PIPELINE_STAGE_SUBTITLE_ICONS } from "./pipelineStageIcons";

const STAGE_ACCENT: Record<PipelineStageId, { accent: string; bg: string }> = {
  upload: { accent: "var(--color-text-tertiary)", bg: "var(--color-bg-tertiary)" },

  map: { accent: "#eab308", bg: "#fefce8" },

  validate: { accent: "#0d9488", bg: "#f0fdfa" },

  resolve: { accent: "#dc2626", bg: "#fef2f2" },

  published: { accent: "#16a34a", bg: "#f0fdf4" },
};

export interface PipelineColumnSelection {
  selectedIds: string[];

  eligibleIds: string[];

  onToggle: (fileId: string) => void;

  onToggleAll: () => void;

  onRunSelected: () => void;

  runLabel: string;

  busy?: boolean;
}

function SelectAllRow({
  stageId,
  selection,
}: {
  stageId: PipelineStageId;
  selection: PipelineColumnSelection;
}) {
  const selectedCount = selection.selectedIds.length;

  const allSelected = selectedCount > 0 && selectedCount === selection.eligibleIds.length;

  const checkboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = selectedCount > 0 && !allSelected;
    }
  }, [selectedCount, allSelected]);

  return (
    <div className="mt-1.5 flex items-center gap-1.5 rounded-md border border-[var(--color-border-light)] bg-[var(--color-bg-secondary)] px-1.5 py-1">
      <input
        ref={checkboxRef}
        type="checkbox"
        checked={allSelected}
        onChange={selection.onToggleAll}
        className="h-3 w-3 shrink-0 accent-[var(--color-button-primary-bg)]"
        aria-label={`Select all ${stageId} files`}
        data-testid={`pipeline-select-all-${stageId}`}
      />

      <span className="min-w-0 flex-1 truncate text-[10px] font-semibold text-[var(--color-text-secondary)]">
        Select all
        <span className="font-medium text-[var(--color-text-tertiary)]">
          {` · ${selectedCount} of ${selection.eligibleIds.length}`}
        </span>
      </span>

      <Button
        type="button"
        size="sm"
        className="h-6 px-1.5 text-[10px]"
        onClick={selection.onRunSelected}
        disabled={selectedCount === 0 || selection.busy}
        data-testid={`pipeline-run-selected-${stageId}`}
      >
        {selection.runLabel}
      </Button>
    </div>
  );
}

interface PipelineColumnProps {
  stageId: PipelineStageId;

  cards: PipelineFileCardModel[];
  pendingActionByFileId?: Record<string, "mapping" | "dqx" | "confirm" | "download">;

  selection?: PipelineColumnSelection;

  onConfirmStructure?: (fileId: string) => void;

  onContinueMapping?: (fileId: string) => void;

  onPreview?: (fileId: string) => void;

  onRemoveFile?: (fileId: string) => void;

  onRunDataQuality?: (fileId: string) => void;

  onViewDqxResults?: (fileId: string) => void;

  onDownloadMapped?: (fileId: string) => void | Promise<void>;
}

export function PipelineColumn({
  stageId,

  cards,
  pendingActionByFileId,

  selection,

  onConfirmStructure,

  onContinueMapping,

  onPreview,

  onRemoveFile,

  onRunDataQuality,

  onViewDqxResults,

  onDownloadMapped,
}: PipelineColumnProps) {
  const stage = PIPELINE_STAGES.find((entry) => entry.id === stageId)!;

  const accent = STAGE_ACCENT[stageId];

  const StageIcon = PIPELINE_STAGE_ICONS[stageId];

  const SubtitleIcon = PIPELINE_STAGE_SUBTITLE_ICONS[stageId];

  return (
    <section
      className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg-secondary)]"
      data-testid={`pipeline-column-${stageId}`}
    >
      <div
        className="relative overflow-hidden rounded-t-lg border-b border-[var(--color-border-light)] px-2 py-1.5"
        style={{ borderTop: `2px solid ${accent.accent}` }}
      >
        <div className="flex items-center gap-1.5">
          <div
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded"
            style={{ color: accent.accent, background: accent.bg }}
          >
            <StageIcon className="h-3 w-3" strokeWidth={1.6} aria-hidden />
          </div>

          <span className="min-w-0 flex-1 truncate text-xs font-bold text-[var(--color-text-primary)]">
            {stage.title}
          </span>

          <span className="rounded-full border border-[var(--color-border-light)] bg-[var(--color-bg-primary)] px-1.5 py-0 font-mono text-[10px] font-semibold">
            {cards.length}
          </span>
        </div>

        <p className="mt-0.5 flex items-start gap-1 line-clamp-2 text-[9px] text-[var(--color-text-tertiary)]">
          <SubtitleIcon className="mt-0.5 h-2.5 w-2.5 shrink-0" strokeWidth={2} aria-hidden />

          <span>{stage.subtitle}</span>
        </p>

        {selection && selection.eligibleIds.length > 0 ? (
          <SelectAllRow stageId={stageId} selection={selection} />
        ) : null}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto p-1.5">
        <AnimatePresence initial={false}>
          {cards.map((card) => (
            <motion.div
              key={card.fileId}
              // Position-only: size changes (e.g. row counts arriving after the
              // board restores) must snap, not squish the card over 0.5s.
              layout="position"
              // Mid-upload placeholders re-key from temp key to real fileId once
              // the upload resolves; animating that exit shows a stale twin card.
              exit={
                card.uploadPhase === "uploading"
                  ? { opacity: 0, transition: { duration: 0 } }
                  : { opacity: 0, scale: 0.75 }
              }
              transition={{ duration: 0.5 }}
            >
              <PipelineFileCard
                card={card}
                pendingAction={pendingActionByFileId?.[card.fileId]}
                selected={selection?.selectedIds.includes(card.fileId) ?? false}
                onToggleSelect={
                  selection && selection.eligibleIds.includes(card.fileId)
                    ? selection.onToggle
                    : undefined
                }
                onConfirmStructure={onConfirmStructure}
                onContinueMapping={onContinueMapping}
                onPreview={onPreview}
                onRemove={onRemoveFile}
                onRunDataQuality={stageId === "validate" ? onRunDataQuality : undefined}
                onViewDqxResults={
                  stageId === "validate" || stageId === "resolve" || stageId === "published"
                    ? onViewDqxResults
                    : undefined
                }
                onDownloadMapped={stageId === "published" ? onDownloadMapped : undefined}
              />
            </motion.div>
          ))}
        </AnimatePresence>

        {cards.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center rounded-md border border-dashed border-[var(--color-border)] px-2 py-4 text-center text-[10px] font-medium text-[var(--color-text-tertiary)]">
            {`No files in ${stage.title.toLowerCase()}`}
          </div>
        ) : null}
      </div>
    </section>
  );
}
